import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useLiveFeed, type FeedStatus } from '../lib/live'
import { ago } from '../lib/format'
import { Icon, type IconName } from './Icon'
import { ErrorBoundary } from './ErrorBoundary'
import { ThemeSwitcher } from './ThemeSwitcher'

/* ===========================================================================
   The shell: icon rail, top bar, telemetry strip.

   The rail is deliberately narrow and icon-only. A dispatcher learns six
   icons in a day and then wants the pixels back for the map.
   =========================================================================== */

interface NavItem {
  to: string
  label: string
  icon: IconName
  /** Hidden unless the principal holds this capability. */
  cap?: string
  section: 'operations' | 'manage' | 'platform'
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'gauge', section: 'operations' },
  { to: '/live', label: 'Live map', icon: 'map', cap: 'live.view', section: 'operations' },
  { to: '/events', label: 'Alerts', icon: 'bell', cap: 'alert.view', section: 'operations' },
  { to: '/reports', label: 'Reports', icon: 'chart', cap: 'report.run', section: 'operations' },

  { to: '/vehicles', label: 'Vehicles', icon: 'truck', cap: 'live.view', section: 'manage' },
  { to: '/drivers', label: 'Drivers', icon: 'user', cap: 'live.view', section: 'manage' },
  { to: '/geofences', label: 'Geofences', icon: 'polygon', cap: 'geofence.view', section: 'manage' },
  { to: '/devices', label: 'Devices', icon: 'chip', cap: 'live.view', section: 'manage' },

  { to: '/tenants', label: 'Accounts', icon: 'building', cap: 'client.manage', section: 'platform' },
  { to: '/users', label: 'Users', icon: 'users', cap: 'user.manage', section: 'platform' },
  { to: '/integrations', label: 'Integrations', icon: 'external', cap: 'apikey.manage', section: 'platform' },
  { to: '/billing', label: 'Billing', icon: 'receipt', cap: 'invoice.view', section: 'platform' },
  { to: '/branding', label: 'Branding', icon: 'palette', cap: 'branding.logo', section: 'platform' },
  { to: '/audit', label: 'Audit log', icon: 'shield', cap: 'audit.read', section: 'platform' },
  { to: '/health', label: 'System health', icon: 'pulse', cap: 'platform.metrics', section: 'platform' },
]

export function Shell() {
  const { user, can, signOut, stopImpersonating, branding } = useAuth()
  const feed = useLiveFeed({ enabled: Boolean(user) })
  const location = useLocation()
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem('sv.rail') === 'open')

  useEffect(() => {
    localStorage.setItem('sv.rail', railOpen ? 'open' : 'closed')
  }, [railOpen])

  const visible = NAV.filter((n) => !n.cap || can(n.cap))
  const sections: NavItem['section'][] = ['operations', 'manage', 'platform']
  const productName = branding?.productName ?? 'SmartVision IoT'

  return (
    <div className="h-full flex flex-col relative z-10">
      {user?.impersonating && <ImpersonationBanner onStop={stopImpersonating} tenant={user.tenantName} />}

      <div className="flex grow min-h-0">
        {/* ---- rail ---- */}
        <nav
          className="shrink-0 flex flex-col rule-r scan-lines transition-[width] duration-200"
          style={{
            width: railOpen ? 196 : 56,
            background: 'var(--color-hull)',
            transitionTimingFunction: 'var(--ease-snap)',
          }}
        >
          <button
            onClick={() => setRailOpen((v) => !v)}
            className="h-12 flex items-center gap-2.5 px-4 rule-b shrink-0 hover:bg-[var(--color-panel)] transition-colors"
            title={railOpen ? 'Collapse' : 'Expand'}
          >
            <Mark />
            {railOpen && (
              <span className="font-[var(--font-label)] font-bold tracking-[0.08em] uppercase text-[13px] truncate">
                {productName}
              </span>
            )}
          </button>

          <div className="grow overflow-y-auto overflow-x-hidden py-2">
            {sections.map((section) => {
              const items = visible.filter((n) => n.section === section)
              if (!items.length) return null
              return (
                <div key={section} className="mb-1.5">
                  {railOpen && (
                    <div className="label px-4 py-2 text-[10px] opacity-70">{section}</div>
                  )}
                  {!railOpen && section !== 'operations' && (
                    <div className="mx-3 my-2 border-t border-[var(--color-rule)]" />
                  )}
                  {items.map((item) => (
                    <RailLink key={item.to} item={item} expanded={railOpen} />
                  ))}
                </div>
              )
            })}
          </div>

        </nav>

        {/* ---- main ---- */}
        <div className="grow flex flex-col min-w-0">
          <TopBar feed={feed} onSignOut={signOut} currentPath={location.pathname} />
          <div
            className="telemetry-strip shrink-0"
            data-stale={feed.status !== 'live'}
            aria-hidden
          />
          <main className="grow min-h-0 overflow-hidden" style={{ background: 'var(--color-void)' }}>
            {/* Keyed on the path so recovering from one broken screen does
                not leave the boundary latched for the next one. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet context={feed} />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  )
}

function RailLink({ item, expanded }: { item: NavItem; expanded: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={expanded ? undefined : item.label}
      className={({ isActive }) =>
        [
          'relative h-9 mx-2 px-2.5 flex items-center gap-3 rounded-[3px] transition-colors duration-100',
          isActive
            ? 'text-[var(--color-ink)] bg-[var(--color-panel)]'
            : 'text-[var(--color-ink-3)] hover:text-[var(--color-ink-2)] hover:bg-[var(--color-panel)]/60',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-[-8px] top-1.5 bottom-1.5 w-[2px] rounded-full"
              style={{ background: 'var(--accent)' }}
            />
          )}
          <Icon name={item.icon} />
          {expanded && <span className="text-[13px] truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  )
}

function TopBar({
  feed,
  onSignOut,
  currentPath,
}: {
  feed: ReturnType<typeof useLiveFeed>
  onSignOut: () => Promise<void>
  currentPath: string
}) {
  const { user, tenant } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => setMenuOpen(false), [currentPath])

  return (
    <header
      className="h-12 shrink-0 flex items-center gap-3 px-4 rule-b"
      style={{ background: 'var(--color-hull)' }}
    >
      <div className="min-w-0 flex items-baseline gap-2.5">
        <span className="label label-bright truncate">{tenant?.name ?? '—'}</span>
        {tenant && (
          <span className="label text-[10px] opacity-60 shrink-0">{tenant.kind}</span>
        )}
      </div>

      <div className="grow" />

      <FeedIndicator status={feed.status} rate={feed.rate} lastPacketAt={feed.lastPacketAt} />

      <ThemeSwitcher compact />

      <div className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2.5 h-8 pl-1 pr-2 rounded-[3px] hover:bg-[var(--color-panel)] transition-colors"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span
            className="w-6 h-6 grid place-items-center rounded-[3px] text-[10px] font-semibold tracking-wide shrink-0"
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
          >
            {initialsOf(user?.fullName ?? '?')}
          </span>
          <span className="hidden sm:block text-[13px] text-[var(--color-ink-2)] max-w-[140px] truncate">
            {user?.fullName}
          </span>
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div
              className="panel panel-glass absolute right-0 top-10 w-60 z-50 py-1"
              style={{ boxShadow: 'var(--shadow-pop)' }}
              role="menu"
            >
              <div className="px-3 py-2 rule-b mb-1">
                <div className="text-[13px] text-[var(--color-ink)] truncate">{user?.fullName}</div>
                <div className="text-[11px] text-[var(--color-ink-3)] truncate">{user?.email}</div>
                <div className="label mt-1.5 text-[10px]">
                  Tier {user?.tier} · {user?.role.replace(/_/g, ' ')}
                </div>
              </div>
              <NavLink
                to="/settings"
                className="block px-3 py-1.5 text-[13px] text-[var(--color-ink-2)] hover:bg-[var(--color-panel-2)] hover:text-[var(--color-ink)]"
                role="menuitem"
              >
                Account settings
              </NavLink>
              <button
                onClick={() => void onSignOut()}
                className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-[var(--color-panel-2)]"
                style={{ color: 'var(--text-critical)' }}
                role="menuitem"
              >
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  )
}

/** The live-feed readout. It answers the question a control room actually
 *  asks — "is this screen still telling me the truth?" — rather than just
 *  showing a green dot that means the page loaded once. */
function FeedIndicator({
  status,
  rate,
  lastPacketAt,
}: {
  status: FeedStatus
  rate: number
  lastPacketAt: number | null
}) {
  const colors: Record<FeedStatus, string> = {
    live: 'var(--color-moving)',
    stale: 'var(--color-idle)',
    connecting: 'var(--accent)',
    offline: 'var(--color-stopped)',
  }
  const textColors: Record<FeedStatus, string> = {
    live: 'var(--text-moving)',
    stale: 'var(--text-idle)',
    connecting: 'var(--accent-text)',
    offline: 'var(--text-stopped)',
  }
  const labels: Record<FeedStatus, string> = {
    live: 'Live',
    stale: 'Quiet',
    connecting: 'Connecting',
    offline: 'Feed down',
  }

  return (
    <div
      className="hidden md:flex items-center gap-2.5 h-7 px-2.5 rounded-[3px] rule-l rule-r"
      style={{ background: 'var(--color-panel)' }}
      title={
        lastPacketAt
          ? `Last packet ${ago(new Date(lastPacketAt).toISOString())}`
          : 'No telemetry received yet'
      }
    >
      <span
        className={`dot ${status === 'live' ? 'dot-live' : ''}`}
        style={{ background: colors[status], color: colors[status] }}
      />
      <span className="label text-[10px]" style={{ color: textColors[status] }}>
        {labels[status]}
      </span>
      {status === 'live' && (
        <span className="tnum text-[11px] text-[var(--color-ink-3)]">
          {rate < 1 ? rate.toFixed(1) : Math.round(rate)}/s
        </span>
      )}
    </div>
  )
}

function ImpersonationBanner({ onStop, tenant }: { onStop: () => Promise<void>; tenant: string }) {
  return (
    <div className="shrink-0 relative z-20">
      <div className="hazard h-1" />
      <div
        className="h-9 flex items-center gap-3 px-4 text-[13px]"
        style={{ background: 'color-mix(in srgb, var(--color-warn) 14%, var(--color-hull))' }}
      >
        <Icon name="shield" />
        <span className="text-[var(--color-ink)]">
          Support session — you are viewing <strong>{tenant}</strong> as its own users see it.
          Everything you do is recorded against this account.
        </span>
        <div className="grow" />
        <button className="btn btn-sm" onClick={() => void onStop()}>
          End session
        </button>
      </div>
      <div className="hazard h-1" />
    </div>
  )
}

function Mark() {
  // The SmartVision aperture: an open outer ring, a satellite dot at the
  // opening, and a central lens — the same mark as the wordmark logo.
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path
        d="M20.69 9.67 A9 9 0 1 1 14.33 3.31"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="18.36" cy="5.64" r="2.1" fill="var(--accent)" />
      <circle cx="12" cy="12" r="3.3" stroke="var(--accent)" strokeWidth="2.2" fill="none" />
    </svg>
  )
}

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}

/** Page scaffolding used by every route below the shell. */
export function Page({
  title,
  subtitle,
  actions,
  children,
  scroll = true,
}: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  scroll?: boolean
}) {
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="shrink-0 flex items-end justify-between gap-4 px-5 pt-5 pb-4">
        <div className="min-w-0">
          <h1 className="text-[1.375rem] font-semibold tracking-tight truncate">{title}</h1>
          {subtitle && (
            <p className="text-[13px] text-[var(--color-ink-3)] mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      <div className={`grow min-h-0 px-5 pb-5 ${scroll ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        {children}
      </div>
    </div>
  )
}
